import { useEffect, useState } from "react";
import { AlertOctagon, ListTree } from "lucide-react";
import { MODES, LANGS } from "@/lib/api";
import { Section, ProvTag, StatusBadge } from "@/components/Brand";
import { Figure } from "@/components/Figure";
import { IcdPicker, IcdLabel } from "@/components/IcdPicker";

const dose = (d = {}) => [d.sets && `${d.sets} sets`, d.reps && `${d.reps} reps`, d.hold_s && `hold ${d.hold_s}s`, d.duration_min && `${d.duration_min} min`, d.frequency_per_week && `${d.frequency_per_week}×/wk`, d.intensity].filter(Boolean).join(" · ") || "—";
export const provOf = (prov, path) => prov?.[path] || Object.entries(prov || {}).find(([k]) => k.startsWith(path + "."))?.[1]
  || Object.entries(prov || {}).filter(([k]) => path.startsWith(k + ".")).sort((a, b) => b[0].length - a[0].length)[0]?.[1];

const Row = ({ k, children }) => (<div className="cv-row grid sm:grid-cols-[150px_1fr] gap-1 py-1 text-[13px]"><span className="eyebrow pt-0.5">{k}</span><div className="cv-val text-slate-700">{children}</div></div>);
const E = () => <span className="empty-val text-slate-400">—</span>;
const L = ({ items }) => (items?.length ? <ul className="list-disc pl-4 space-y-0.5">{items.map((x, i) => <li key={i}>{x}</li>)}</ul> : <E />);
const T = ({ v }) => (v ? <span className="whitespace-pre-line">{v}</span> : <E />);
const PC_LABELS = { summary: "Summary", when_to_stop: "When to stop", home_programme_notes: "Home programme notes" };

const SECTIONS = [["sec-overview", "Overview"], ["sec-phases", "Phases"], ["sec-safety", "Safety layer"], ["sec-outcomes", "Outcome measures"], ["sec-evidence", "Evidence & provenance"], ["sec-variants", "Variants & jurisdiction"], ["sec-patient", "Patient-facing content"]];
const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

function useActive(ids) {
  const [active, setActive] = useState(ids[0]);
  const key = ids.join("|");
  useEffect(() => {
    const obs = new IntersectionObserver((es) => {
      const vis = es.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (vis[0]) setActive(vis[0].target.id);
    }, { rootMargin: "-70px 0px -65% 0px" });
    key.split("|").forEach((id) => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [key]);
  return active;
}

function JumpMenu({ phases, ivs }) {
  const ids = ["sec-overview", ...phases.flatMap((ph, i) => [`phase-${i}`, ...(ph.items || []).map((_, j) => `phase-${i}-ex-${j}`)]), ...SECTIONS.slice(2).map((s) => s[0])];
  const active = useActive(ids);
  const cls = (id, base) => `${base} block w-full truncate rounded px-2 py-1 text-left transition-colors ${active === id ? "bg-sky-100 text-sky-800 font-semibold" : "text-slate-600 hover:bg-slate-100"}`;
  return (
    <nav className="no-print sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto rounded border border-slate-200 bg-white p-2 text-[12px]" data-testid="jump-menu">
      <div className="eyebrow mb-1 flex items-center gap-1 px-2"><ListTree size={12} />Jump to</div>
      <button data-testid="jump-sec-overview" onClick={() => go("sec-overview")} className={cls("sec-overview", "")}>Overview</button>
      {phases.map((ph, i) => (
        <div key={ph.id || i}>
          <button data-testid={`jump-phase-${i}`} onClick={() => go(`phase-${i}`)} className={cls(`phase-${i}`, "mt-0.5")}>{ph.order ?? i + 1}. {ph.name}</button>
          {(ph.items || []).map((it, j) => (
            <button key={j} data-testid={`jump-phase-${i}-ex-${j}`} onClick={() => go(`phase-${i}-ex-${j}`)} title={ivs[it.intervention_id]?.name}
              className={cls(`phase-${i}-ex-${j}`, "pl-5 text-[11px]")}>{ivs[it.intervention_id]?.name || "Exercise"}</button>
          ))}
        </div>
      ))}
      {SECTIONS.slice(2).map(([id, l]) => <button key={id} data-testid={`jump-${id}`} onClick={() => go(id)} className={cls(id, "mt-0.5")}>{l}</button>)}
    </nav>
  );
}

function JumpSelect({ phases, ivs }) {
  return (
    <select data-testid="jump-select" defaultValue="" onChange={(e) => { go(e.target.value); e.target.value = ""; }} className="no-print sticky top-16 z-10 w-full rounded border border-slate-200 bg-white px-2 py-2 text-[13px] shadow-sm lg:hidden">
      <option value="" disabled>Jump to…</option>
      <option value="sec-overview">Overview</option>
      {phases.map((ph, i) => [
        <option key={`p${i}`} value={`phase-${i}`}>{ph.order ?? i + 1}. {ph.name}</option>,
        ...(ph.items || []).map((it, j) => <option key={`p${i}e${j}`} value={`phase-${i}-ex-${j}`}> {ivs[it.intervention_id]?.name || "Exercise"}</option>),
      ])}
      {SECTIONS.slice(2).map(([id, l]) => <option key={id} value={id}>{l}</option>)}
    </select>
  );
}

function IcdRow({ labels, onSave }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(labels);
  useEffect(() => { setVal(labels); }, [labels]);
  return (
    <Row k="ICD-10">
      {edit ? (
        <div className="space-y-2"><IcdPicker testid="icd-edit" value={val} onChange={setVal} />
          <div className="flex gap-2"><button data-testid="icd-save-btn" onClick={async () => { if (await onSave(val.map((x) => x.code))) setEdit(false); }} className="rounded bg-sky-600 px-3 py-1 text-[12px] font-semibold text-white">Save codes</button>
            <button data-testid="icd-cancel-btn" onClick={() => { setVal(labels); setEdit(false); }} className="rounded border border-slate-300 px-3 py-1 text-[12px]">Cancel</button></div></div>
      ) : (
        <div className="space-y-0.5">
          {labels.length ? labels.map((x) => <div key={x.code}><IcdLabel x={x} testid={`icd-label-${x.code}`} /></div>) : <span className="text-slate-400">—</span>}
          {onSave && <button data-testid="icd-edit-btn" onClick={() => setEdit(true)} className="no-print text-[11px] font-semibold text-sky-700 hover:underline">Choose codes</button>}
        </div>
      )}
      <span className="mt-0.5 block text-[11px] text-slate-500" data-testid="icd-who-note">PranIQ uses the WHO International Classification of Diseases (ICD-10) standard codes so every condition is named the same way everywhere.</span>
    </Row>
  );
}

function Exercise({ iv, item, ills, testid, id }) {
  const c = iv?.content || {};
  return (
    <div id={id} className="scroll-mt-20 rounded border border-slate-200 bg-slate-50/60 p-3" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2">
        <h5 className="font-heading text-[14px] font-bold text-slate-900">{iv?.name || item.intervention_id}</h5>
        <span className="font-mono text-[11px] text-slate-500">pinned v{item.intervention_version}</span>
        {iv && iv.current_version > item.intervention_version && <span className="text-[11px] text-amber-600">v{iv.current_version} available</span>}
        <span className="ml-auto rounded bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-800">{dose(item.dosage)}</span>
      </div>
      {item.notes && <Row k="Notes"><T v={item.notes} /></Row>}
      {c.summary && <Row k="Summary">{c.summary}</Row>}
      {(c.steps || []).length > 0 && <ol className="mt-2 space-y-2">{c.steps.map((st, k) => (
        <li key={k} className="flex gap-3 rounded bg-white p-2" data-testid={`${testid}-step-${k}`}>
          <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-slate-800 text-[10px] font-bold text-white">{st.order ?? k + 1}</span>
          <div className="min-w-0 flex-1 text-[13px]"><span className="eyebrow mr-1">{st.stage}</span>{st.text}
            {!(st.illustration_ids || []).length && st.photo_needed && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-800">Clinician photo needed</span>}</div>
          <div className="flex shrink-0 gap-1">{(st.illustration_ids || []).filter((id) => ills[id]).map((id) => (
            <div key={id} className="text-center"><Figure ill={ills[id]} className="h-20 w-20" testid={`${testid}-fig-${k}-${id.slice(0, 6)}`} />
              <StatusBadge status={ills[id].status} testid={`${testid}-fig-status-${k}-${id.slice(0, 6)}`} /></div>
          ))}</div>
        </li>
      ))}</ol>}
      <Row k="Cues"><L items={c.cues} /></Row>
      <Row k="Should feel"><T v={c.feel} /></Row>
      <Row k="Should not feel"><T v={c.should_not_feel} /></Row>
      <Row k="Common errors"><L items={(c.common_errors || []).map((e) => `${e.error} → ${e.correction}`)} /></Row>
      <Row k="Safety stops"><L items={c.safety_stops} /></Row>
      {(c.precautions || []).length > 0 && <Row k="Precautions"><L items={c.precautions} /></Row>}
      <Row k="Progression"><L items={c.progression} /></Row>
      <Row k="Regression"><L items={c.regression} /></Row>
      <Row k="Equipment">{[c.equipment_home && `Home: ${c.equipment_home}`, c.equipment_clinic && `Clinic: ${c.equipment_clinic}`].filter(Boolean).join(" · ") || <E />}</Row>
      {(c.settings || []).length > 0 && <Row k="Settings">{c.settings.join(", ")}</Row>}
      {(c.media_refs || []).length > 0 && <Row k="Media"><L items={c.media_refs.map((m) => m.title || m.url)} /></Row>}
    </div>
  );
}

export default function ContentView({ v, onIcdSave }) {
  const c = v.resolved_content || v.content;
  const pv = v.provenance;
  const ivs = v.interventions || {};
  const ills = v.illustrations || {};
  const phases = c.phases || [];
  return (
    <div className="lg:grid lg:grid-cols-[210px_minmax(0,1fr)] lg:gap-4">
      <aside className="no-print hidden lg:block"><JumpMenu phases={phases} ivs={ivs} /></aside>
    <div className="min-w-0 space-y-4" data-testid="content-view">
      <JumpSelect phases={phases} ivs={ivs} />
      <div id="sec-overview" className="scroll-mt-20"><Section title="Overview">
        <Row k="Indication"><T v={c.indication} /> <ProvTag prov={provOf(pv, "indication")} /></Row>
        <Row k="Population"><T v={c.target_population} /></Row>
        <Row k="Care modes">{(c.care_modes || []).map((m) => MODES[m]).join(", ")}</Row>
        <IcdRow labels={v.protocol?.icd10_labels || []} onSave={onIcdSave} />
      </Section></div>
      <div id="sec-phases" className="scroll-mt-20"><Section title="Phases" testid="phases-view">
        <div className="space-y-4">
          {phases.map((ph, i) => (
            <div key={ph.id} id={`phase-${i}`} className="scroll-mt-20 rounded border border-slate-200 p-3" data-testid={`phase-view-${i}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="grid h-6 w-6 place-items-center rounded-full bg-sky-600 text-[11px] font-bold text-white">{ph.order ?? i + 1}</span>
                <h4 className="font-heading font-bold text-slate-900">{ph.name}</h4>
                <span className="font-mono text-[11px] text-slate-500">{ph.duration_min_weeks == null && ph.duration_max_weeks == null ? "duration set by the treating Clinician" : `weeks ${ph.duration_min_weeks ?? "?"}–${ph.duration_max_weeks ?? "?"}`}</span>
                <ProvTag prov={provOf(pv, `phases.${i}`)} />
              </div>
              <Row k="Goals"><L items={ph.goals} /></Row>
              <Row k="Entry"><L items={ph.entry_criteria} /></Row>
              <Row k="Exit / progress"><L items={ph.exit_criteria} /></Row>
              <Row k="Stop / regress"><L items={ph.stop_criteria} /></Row>
              <div className="mt-2 space-y-2">{(ph.items || []).map((it, j) => (
                <Exercise key={j} id={`phase-${i}-ex-${j}`} iv={ivs[it.intervention_id]} item={it} ills={ills} testid={`phase-${i}-exercise-${j}`} />
              ))}</div>
            </div>
          ))}
        </div>
      </Section></div>
      <div id="sec-safety" className="scroll-mt-20"><Section title="Safety layer" testid="safety-view">
        <Row k="Contraindications"><L items={c.safety?.contraindications} /></Row>
        <Row k="Precautions"><L items={c.safety?.precautions} /></Row>
        <div className="mt-2 rounded border border-red-300 bg-red-50 p-3" data-testid="red-flags-box">
          <div className="flex items-center gap-2 text-[13px] font-bold text-red-800"><AlertOctagon size={15} />Red flags — required action</div>
          <ul className="mt-1 space-y-1 text-[13px] text-red-800">{(c.safety?.red_flags || []).map((r, i) => <li key={i}><b>{r.flag}</b> → {r.action}</li>)}</ul>
        </div>
        <Row k="Escalation">{c.safety?.escalation}</Row>
      </Section></div>
      <div id="sec-outcomes" className="scroll-mt-20"><Section title="Outcome measures">
        <ul className="text-[13px] space-y-1">{(c.outcome_measures || []).map((o, i) => <li key={i}><b className="font-mono">{o.code}</b> · {o.timepoints} · expected: {o.expected}{o.mcid ? ` · MCID ${o.mcid}` : ""}</li>)}</ul>
      </Section></div>
      <div id="sec-evidence" className="scroll-mt-20"><Section title="Evidence & provenance">
        <Row k="Guideline">{c.evidence?.guideline_source}</Row>
        <Row k="Grade">{c.evidence?.grade}</Row>
        <Row k="Author credentials">{c.evidence?.author_credentials || <E />}</Row>
        <Row k="Last review">{c.evidence?.last_review}</Row>
        <Row k="Next review due"><span className="font-mono">{c.evidence?.next_review_due}</span></Row>
        <Row k="References"><ul className="list-disc pl-4 space-y-0.5">{(c.evidence?.references || []).map((r, i) => <li key={i}>{r.citation}{r.url && <> · <a href={r.url} target="_blank" rel="noreferrer" className="text-sky-700 underline break-all">{r.url}</a></>}</li>)}</ul></Row>
        {(c.clinical_placeholders || []).length > 0 && <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-[12px] text-amber-800" data-testid="placeholders-box">
          {(c.clinical_placeholders || []).map((ph, i) => <div key={i}>{ph.resolved ? "✓" : "⚑"} {ph.note}</div>)}</div>}
      </Section></div>
      <div id="sec-variants" className="scroll-mt-20"><Section title="Variants, care modes & jurisdiction">
        {(c.variants || []).map((x, i) => <Row key={i} k={`${x.type}: ${x.label}`}>{x.adjustments}</Row>)}
        {Object.entries(c.mode_variations || {}).map(([m, t]) => <Row key={m} k={MODES[m] || m}>{t}</Row>)}
        {Object.entries(c.jurisdiction_notes || {}).map(([m, t]) => <Row key={m} k={`Market ${m}`}>{t}</Row>)}
      </Section></div>
      <div id="sec-patient" className="scroll-mt-20"><Section title="Patient-facing content">
        {Object.entries(c.patient_content || {}).map(([lg, pc]) => (
          <div key={lg} className="border-t border-slate-100 py-2 first:border-0" data-testid={`patient-content-${lg}`}>
            <div className="font-semibold text-[13px] text-slate-800">{LANGS[lg] || lg}</div>
            {Object.entries(pc || {}).map(([k, t]) => <Row key={k} k={PC_LABELS[k] || k.replace(/_/g, " ")}>{Array.isArray(t) ? <L items={t.map((x) => (typeof x === "object" ? Object.values(x).join(" – ") : x))} /> : <T v={typeof t === "object" && t ? Object.values(t).join(" · ") : t} />}</Row>)}
          </div>
        ))}
      </Section></div>
    </div>
    </div>
  );
}
